/* eslint-disable no-console */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import * as rax from "retry-axios";
import { OktaAuth } from "@okta/okta-auth-js";
import { IAppConfig } from "../shared/types/config.types";
import {
  LOCAL_BASE_URL,
  NOTIFICATIONS_ENDPOINT,
  PACKAGES_ENDPOINT,
} from "./constants/urls.constants";
import * as Models from "./models";
import APIUtils, { EApiTypes } from "./utils.api";
import { IProjectsAPI } from "./projects.service";
import { PersistentMockProjectsService } from "./mock/api.service";
import { IStandardError } from "../shared/types/error.types";
import errorConstants from "./constants/error.constants";
import { MockAPISettings } from "./mock/mock.types";
import { APIArgs, APIDetailsArgs } from "./api.types";
import { convertError } from "../shared/utils/errorHandling.utils";

export class ApiService {
  private static instance: ApiService;
  private _appConfig!: IAppConfig;
  private _projectsApi!: IProjectsAPI;
  private projectsAxiosInstance!: AxiosInstance;
  private _oktaInstance: OktaAuth | null = null;

  public getBaseUrl(): string {
    let baseURL;
    if (
      process.env.NODE_ENV === "development" ||
      process.env.NODE_ENV === "test"
    ) {
      if (process.env.REACT_APP_ENABLE_LOCAL === "true") {
        baseURL = LOCAL_BASE_URL;
      } else {
        baseURL = this.appConfig.apiUrl;
      }
    } else {
      /* istanbul ignore next */
      baseURL = this.appConfig.apiUrl;
    }
    return baseURL;
  }

  private buildAxiosInstance(requiresAuth: boolean): AxiosInstance {
    const instance: AxiosInstance = axios.create({
      baseURL: this.getBaseUrl(),
      headers: {
        "Ocp-Apim-Subscription-Key": this.appConfig.apimSubscriptionKey,
      },
    });
    instance.defaults.raxConfig = {
      instance,
      statusCodesToRetry: [[0], [429], [599]],
      httpMethodsToRetry: ["GET", "HEAD", "OPTIONS"],
      shouldRetry: (err: AxiosError<any>) =>
        APIUtils.handleShouldRetry(err, rax),
      onRetryAttempt: (err) => APIUtils.handleRetryAttempt(err, rax),
    };

    rax.attach(instance);

    if (requiresAuth) {
      instance.interceptors.request.use(
        async (reqConfig: AxiosRequestConfig) => {
          if (this._oktaInstance) {
            let headers: Record<string, unknown>;
            /* istanbul ignore next */
            if (!reqConfig.headers) {
              headers = {};
              reqConfig.headers = headers;
            } else if (typeof reqConfig.headers === "object") {
              headers = reqConfig.headers as Record<string, unknown>;
            } else {
              /* istanbul ignore next */ return reqConfig;
            }

            if (!headers["Authorization"]) {
              headers[
                "Authorization"
              ] = `Bearer ${this._oktaInstance.getAccessToken()}`;
            }
          }
          return reqConfig;
        }
      );
    }

    return instance;
  }

  private processAxiosError<T>(error: AxiosError<T> | unknown): IStandardError {
    /* istanbul ignore next */
    if (typeof error === "object") {
      return convertError(error as object);
    } else {
      throw error;
    }
  }

  private async submitAxiosGetRequest<TResult>(
    instance: AxiosInstance,
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<TResult>> {
    try {
      const result = await instance.get<TResult>(url, config);
      return result;
    } catch (error: AxiosError<TResult> | unknown) {
      /* istanbul ignore next */ throw this.processAxiosError(error);
    }
  }

  private initProjectsApi(): void {
    this.projectsAxiosInstance = this.buildAxiosInstance(true);
    this._projectsApi = {
      getProjects: async (
        args?: Models.GetProjectsParameters
      ): Promise<Models.GetProjectsResponse> => {
        try {
          const result =
            await this.submitAxiosGetRequest<Models.GetProjectsResponse>(
              this.projectsAxiosInstance,
              `${APIUtils.getEndpoint(EApiTypes.PROJECTS)}`,
              { params: args }
            );
          return result.data;
        } catch (error: AxiosError<Models.GetProjectsResponse> | unknown) {
          /* istanbul ignore next */ throw this.processAxiosError(error);
        }
      },
      getProjectDetails: async (
        args: APIArgs
      ): Promise<Models.GetProjectDetailsResponse> => {
        if (args.projectId.length === 0) {
          throw errorConstants.BAD_REQUEST_ERROR;
        }
        try {
          const result =
            await this.submitAxiosGetRequest<Models.GetProjectDetailsResponse>(
              this.projectsAxiosInstance,
              `${APIUtils.getEndpoint(EApiTypes.PROJECTS)}/${encodeURIComponent(
                args.projectId
              )}/`
            );
          return result.data;
        } catch (error:
          | AxiosError<Models.GetProjectDetailsResponse>
          | unknown) {
          /* istanbul ignore next */ throw this.processAxiosError(error);
        }
      },
      getProjectPackages: async (
        args: APIArgs
      ): Promise<Models.GetProjectPackagesResponse> => {
        if (args.projectId.length === 0) {
          throw errorConstants.BAD_REQUEST_ERROR;
        }
        try {
          const result =
            await this.submitAxiosGetRequest<Models.GetProjectPackagesResponse>(
              this.projectsAxiosInstance,
              `${APIUtils.getEndpoint(EApiTypes.PROJECTS)}/${encodeURIComponent(
                args.projectId
              )}${PACKAGES_ENDPOINT}`
            );
          return result.data;
        } catch (error:
          | AxiosError<Models.GetProjectPackagesResponse>
          | unknown) {
          /* istanbul ignore next */ throw this.processAxiosError(error);
        }
      },
      getPackageDetails: async (
        args: APIDetailsArgs
      ): Promise<Models.GetPackageDetailsResponse> => {
        if (args.projectId.length === 0 || args.packageId.length === 0) {
          throw errorConstants.BAD_REQUEST_ERROR;
        }
        try {
          const result =
            await this.submitAxiosGetRequest<Models.GetPackageDetailsResponse>(
              this.projectsAxiosInstance,
              `${APIUtils.getEndpoint(EApiTypes.PROJECTS)}/${encodeURIComponent(
                args.projectId
              )}${PACKAGES_ENDPOINT}/${encodeURIComponent(args.packageId)}`
            );
          return result.data;
        } catch (error:
          | AxiosError<Models.GetPackageDetailsResponse>
          | unknown) {
          /* istanbul ignore next */ throw this.processAxiosError(error);
        }
      },

      getPackageNotifications: async (
        args: APIDetailsArgs
      ): Promise<Models.GetPackageNotificationsResponse> => {
        if (args.projectId.length === 0 || args.packageId.length === 0) {
          throw errorConstants.BAD_REQUEST_ERROR;
        }
        try {
          const result =
            await this.submitAxiosGetRequest<Models.GetPackageNotificationsResponse>(
              this.projectsAxiosInstance,
              `${APIUtils.getEndpoint(EApiTypes.PROJECTS)}/${encodeURIComponent(
                args.projectId
              )}${PACKAGES_ENDPOINT}/${encodeURIComponent(
                args.packageId
              )}${NOTIFICATIONS_ENDPOINT}`
            );
          return result.data;
        } catch (error:
          | AxiosError<Models.GetPackageNotificationsResponse>
          | unknown) {
          /* istanbul ignore next */ throw this.processAxiosError(error);
        }
      },
    };
  }

  private initProjectsMockApi(config?: MockAPISettings): void {
    this._projectsApi = new PersistentMockProjectsService(config);
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }

    return ApiService.instance;
  }

  public initializeAPIs(): void {
    this.initProjectsApi();
  }

  public initializeMockAPIs(config?: MockAPISettings): void {
    this.initProjectsMockApi(config);
  }

  public set appConfig(appConfig: IAppConfig) {
    this._appConfig = appConfig;
  }

  public get appConfig(): IAppConfig {
    return this._appConfig;
  }

  public get oktaAuth(): OktaAuth | null {
    return this._oktaInstance;
  }

  public set oktaAuth(okta: OktaAuth | null) {
    this._oktaInstance = okta;
  }

  public get projectsApi(): IProjectsAPI {
    return this._projectsApi;
  }

  public get initialized(): boolean {
    return !!this._projectsApi;
  }
}
